import React from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import { useNotifications } from '../../hooks/useNotifications';

export type LocaleCode = 'pt' | 'en' | 'es';

type I18nTripleBlock = {
  question?: string; answer?: string; title?: string; content?: string; excerpt?: string; description?: string;
  corpoTexto?: string; nome?: string;
  headline?: string; subheadline?: string; bodyHtml?: string; ctaLabel?: string;
  metaTitle?: string; metaDescription?: string; displayName?: string;
  urgencyText?: string; ctaMicrocopy?: string;
  publicTitle?: string;
};

export type I18nTriple = {
  pt: I18nTripleBlock;
  en: I18nTripleBlock;
  es: I18nTripleBlock;
};

type FieldMode = 'faq' | 'post' | 'social' | 'orcamento' | 'servico' | 'landingPage' | 'avatar' | 'agendamento';

interface I18nTripleFieldsProps {
  mode: FieldMode;
  value: I18nTriple;
  onChange: (next: I18nTriple) => void;
}

const LOCALES: { code: LocaleCode; labelKey: string }[] = [
  { code: 'pt', labelKey: 'admin.langPt' },
  { code: 'en', labelKey: 'admin.langEn' },
  { code: 'es', labelKey: 'admin.langEs' }
];

const I18nTripleFields: React.FC<I18nTripleFieldsProps> = ({ mode, value, onChange }) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [active, setActive] = React.useState<LocaleCode>('pt');
  const [translating, setTranslating] = React.useState(false);

  const patch = (lang: LocaleCode, field: string, fieldValue: string) => {
    onChange({
      ...value,
      [lang]: { ...value[lang], [field]: fieldValue }
    });
  };

  const block = value[active] || {};

  const canTranslate = (mode === 'servico' || mode === 'orcamento' || mode === 'agendamento') && active !== 'pt';
  const sourceFilled =
    mode === 'servico'
      ? !!value.pt?.nome
      : mode === 'agendamento'
        ? !!(value.pt?.publicTitle || value.pt?.description)
        : !!value.pt?.corpoTexto;

  const handleTranslate = async () => {
    try {
      setTranslating(true);
      if (mode === 'servico') {
        const res = await axios.post('/servico/translate', { nome: value.pt?.nome || '' });
        const { en, es } = res.data;
        onChange({
          ...value,
          en: { ...value.en, nome: en },
          es: { ...value.es, nome: es }
        });
        notifications.showSuccess('Tradução gerada!', 'Revise o nome em inglês e espanhol antes de salvar.');
      } else if (mode === 'orcamento') {
        const res = await axios.post('/orcamento/translate', { corpoTexto: value.pt?.corpoTexto || '' });
        const { en, es } = res.data;
        onChange({
          ...value,
          en: { ...value.en, corpoTexto: en },
          es: { ...value.es, corpoTexto: es }
        });
        notifications.showSuccess('Tradução gerada!', 'Revise o texto em inglês e espanhol antes de salvar.');
      } else if (mode === 'agendamento') {
        const res = await axios.post('/agendamento/translate', {
          publicTitle: value.pt?.publicTitle || '',
          description: value.pt?.description || ''
        });
        const { en, es } = res.data;
        onChange({
          ...value,
          en: { ...value.en, ...en },
          es: { ...value.es, ...es }
        });
        notifications.showSuccess('Tradução gerada!', 'Revise o título e a descrição em inglês e espanhol antes de salvar.');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao traduzir', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2">
        <div className="flex flex-wrap gap-2">
          {LOCALES.map((loc) => (
            <button
              key={loc.code}
              type="button"
              onClick={() => setActive(loc.code)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                active === loc.code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t(loc.labelKey)}
            </button>
          ))}
        </div>
        {canTranslate && (
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translating || !sourceFilled}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {translating ? 'Traduzindo...' : 'Traduzir com IA'}
          </button>
        )}
      </div>

      {mode === 'landingPage' ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
            <input
              type="text"
              value={block.headline || ''}
              onChange={(e) => patch(active, 'headline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subheadline</label>
            <input
              type="text"
              value={block.subheadline || ''}
              onChange={(e) => patch(active, 'subheadline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Aviso de urgência/escassez (opcional)</label>
            <input
              type="text"
              value={block.urgencyText || ''}
              onChange={(e) => patch(active, 'urgencyText', e.target.value)}
              placeholder="Ex.: Vagas limitadas neste mês"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Texto do botão (CTA)</label>
            <input
              type="text"
              value={block.ctaLabel || ''}
              onChange={(e) => patch(active, 'ctaLabel', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Microtexto abaixo do botão (opcional)</label>
            <input
              type="text"
              value={block.ctaMicrocopy || ''}
              onChange={(e) => patch(active, 'ctaMicrocopy', e.target.value)}
              placeholder="Ex.: Sem compromisso — resposta em até 24h"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Texto adicional (opcional)</label>
            <RichTextEditor
              value={block.bodyHtml || ''}
              onChange={(html) => patch(active, 'bodyHtml', html)}
              placeholder="Detalhes extras da oferta..."
              minHeight="120px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título para SEO (meta title)</label>
            <input
              type="text"
              value={block.metaTitle || ''}
              onChange={(e) => patch(active, 'metaTitle', e.target.value)}
              maxLength={60}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição para SEO (meta description)</label>
            <textarea
              value={block.metaDescription || ''}
              onChange={(e) => patch(active, 'metaDescription', e.target.value)}
              rows={2}
              maxLength={155}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </>
      ) : mode === 'agendamento' ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('agendamentoAdmin.publicTitle')}</label>
            <input
              type="text"
              value={block.publicTitle || ''}
              onChange={(e) => patch(active, 'publicTitle', e.target.value)}
              placeholder={t('agendamentoAdmin.publicTitlePh') as string}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('agendamentoAdmin.description')}</label>
            <textarea
              value={block.description || ''}
              onChange={(e) => patch(active, 'description', e.target.value)}
              placeholder={t('agendamentoAdmin.descriptionPh') as string}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </>
      ) : mode === 'avatar' ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.avatarDisplayName')}</label>
            <input
              type="text"
              value={block.displayName || ''}
              onChange={(e) => patch(active, 'displayName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.avatarHeadline')}</label>
            <input
              type="text"
              value={block.headline || ''}
              onChange={(e) => patch(active, 'headline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.avatarBody')}</label>
            <RichTextEditor
              value={block.bodyHtml || ''}
              onChange={(html) => patch(active, 'bodyHtml', html)}
              placeholder={t('admin.avatarBodyPh') as string}
              minHeight="120px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.avatarCtaLabel')}</label>
            <input
              type="text"
              value={block.ctaLabel || ''}
              onChange={(e) => patch(active, 'ctaLabel', e.target.value)}
              placeholder={t('admin.avatarCtaPh') as string}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </>
      ) : mode === 'orcamento' ? (
        <RichTextEditor
          value={block.corpoTexto || ''}
          onChange={(html) => patch(active, 'corpoTexto', html)}
          placeholder="Descreva o escopo, condições ou observações do orçamento..."
          minHeight="180px"
        />
      ) : mode === 'servico' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
          <input
            type="text"
            value={block.nome || ''}
            onChange={(e) => patch(active, 'nome', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            required={active === 'pt'}
          />
        </div>
      ) : mode === 'social' ? (
        <>
          {active !== 'pt' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.socialTitle')}</label>
              <input
                type="text"
                value={block.title || ''}
                onChange={(e) => patch(active, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.socialDescription')}</label>
            <textarea
              value={block.description || ''}
              onChange={(e) => patch(active, 'description', e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
        </>
      ) : mode === 'faq' ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.faqQuestion')}</label>
            <input
              type="text"
              value={block.question || ''}
              onChange={(e) => patch(active, 'question', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.faqAnswer')}</label>
            <textarea
              value={block.answer || ''}
              onChange={(e) => patch(active, 'answer', e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
        </>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.postTitle')}</label>
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => patch(active, 'title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              required={active === 'pt'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.postExcerpt')}</label>
            <textarea
              value={block.excerpt || ''}
              onChange={(e) => patch(active, 'excerpt', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.postContent')}</label>
            <textarea
              value={block.content || ''}
              onChange={(e) => patch(active, 'content', e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 font-mono text-sm"
              required={active === 'pt'}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default I18nTripleFields;
